'use client';
import React, { useMemo } from 'react';
import {
  Typography,
  Button,
  Dialog,
  Card,
  CardHeader,
  CardMedia,
  CardContent,
  CardActions,
  SxProps,
  Theme,
  DialogTitle,
  DialogContent,
  Stack,
} from '@mui/material';
import { File, EntityItem } from '@peerless/data-access/strapi';
import { RouterLink } from '@/navigation';
import { useTranslations } from 'next-intl';
import { formatMediaUrl, isValidUrl } from '../utils';
import { formatRegionalDate } from '../utils/formatRegionalDate';
import { useCurrentLocale } from '@peerless/ui-locale';

interface IconCardProps {
  image?: EntityItem<File>;
  title: string;
  description: string;
  externalUrlLink?: string;
  pageUrlLink: string;
  internalDocumentLink?: EntityItem<File>;
  showBookTimeButton: boolean;
  modalContent?: React.JSX.Element;
  sx?: SxProps<Theme>;
  date?: string;
  endDate?: string;
  subHeader?: string;
}

export const TradeShowPreviewCard: React.FC<IconCardProps> = ({
  image,
  title,
  description,
  externalUrlLink,
  internalDocumentLink,
  pageUrlLink,
  showBookTimeButton,
  modalContent,
  sx,
  date,
  endDate,
  subHeader,
}) => {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const t = useTranslations();
  const { locale } = useCurrentLocale();
  const localizedDate = useMemo(() => {
    if (!date) return null;
    return formatRegionalDate(date, locale);
  }, [date, locale]);
  const localizedEndDate = useMemo(() => {
    if (!endDate) return null;
    return formatRegionalDate(endDate, locale);
  }, [endDate, locale]);
  const dateString =
    !localizedEndDate || localizedDate === localizedEndDate
      ? localizedDate
      : `${localizedDate} - ${localizedEndDate}`;
  const mediaUrl = internalDocumentLink?.data?.attributes
    ? formatMediaUrl(internalDocumentLink.data.attributes)
    : pageUrlLink;
  const url = externalUrlLink ?? mediaUrl;
  const target = isValidUrl(url) ? '_blank' : '_self';
  return (
    <Card sx={sx} variant="cardBorder">
      <CardHeader sx={{ display: 'none' }} />
      {image?.data?.attributes && (
        <CardMedia component={'img'} alt={title} image={formatMediaUrl(image.data.attributes)} />
      )}
      <CardContent sx={{ paddingBottom: '0' }}>
        <Typography variant={'h3'} component={'div'} marginBottom={0}>
          {title}
        </Typography>
        <Stack>
          {subHeader ? (
            <Typography sx={{ fontWeight: 'bold' }} component={'div'}>
              {subHeader}
            </Typography>
          ) : null}
          <Typography component={'div'}>{dateString}</Typography>
        </Stack>
        {description && <Typography paddingTop={2}>{description}</Typography>}
      </CardContent>
      <CardActions sx={{ flexDirection: 'column', gap: 1, spacing: 0 }}>
        {showBookTimeButton && (
          <>
            <Button fullWidth variant="outlined" color="secondary" onClick={handleOpen}>
              {t('Form.book-time')}
            </Button>
            <Dialog
              open={open}
              keepMounted
              onClose={handleClose}
              aria-describedby="alert-dialog-slide-description"
              maxWidth={'md'}
              fullWidth
            >
              <DialogTitle id="alert-dialog-title">
                <Typography component={'h2'} variant="h2" className="header-underline">
                  {t('Form.book-time')}
                </Typography>
              </DialogTitle>
              <DialogContent>{modalContent}</DialogContent>
            </Dialog>
          </>
        )}
        <Button
          fullWidth
          variant="outlined"
          href={url}
          component={RouterLink}
          target={target}
          sx={{ marginLeft: '0 !important' }}
        >
          {t('Search.link-here')}
        </Button>
      </CardActions>
    </Card>
  );
};
